import { Button, Icon, Spinner, Typography } from "@/components/atoms";

export interface ReviewRow {
  label: string;
  value: React.ReactNode;
}

const ORIENTATION_LABELS: Record<string, string> = {
  front: "Front",
  rear: "Rear",
  left: "Left side",
  right: "Right side",
  "left-side": "Left side",
  "right-side": "Right side",
};

/**
 * Step 5 (DEV-1903): everything once, with what the walk-around captured, and
 * "Add vehicle · $X" — the click that creates the vehicle and charges its fee.
 */
export const ReviewStep = ({
  rows,
  photos,
  feeLabel,
  error,
  onBack,
  onConfirm,
  onFinishLater,
  saving,
}: {
  rows: ReviewRow[];
  photos: Array<{ orientation: string; url: string }>;
  /** "$5.00"; null when this operator pays no fee. */
  feeLabel: string | null;
  /** Why the last attempt did not add the vehicle, and what to do. */
  error: React.ReactNode;
  onBack: () => void;
  onConfirm: () => void;
  onFinishLater: () => void;
  saving: boolean;
}) => (
  <div className="flex flex-col gap-6">
    <div className="rounded-lg bg-neutral-surface-gray p-4 text-center">
      <Typography variant="paragraph" className="text-neutral-dark-gray">
        Check the details below. When you click &apos;{feeLabel ? `Add vehicle · ${feeLabel}` : "Add vehicle"}&apos; we add the vehicle
        {feeLabel ? " and charge its verification fee" : ""} — it&apos;s ready for bookings straight after.
      </Typography>
    </div>

    {error}

    <dl className="grid grid-cols-2 gap-3">
      {rows.map((row) => (
        <div key={row.label} className="contents">
          <dt>
            <Typography className="text-neutral-dark-gray">{row.label}</Typography>
          </dt>
          <dd className="text-right">
            <Typography className="text-neutral-black">{row.value ?? "-"}</Typography>
          </dd>
        </div>
      ))}
    </dl>

    <div className="rounded-lg border border-neutral-mid-gray p-4">
      <Typography variant="action">What was captured</Typography>
      {photos.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {photos.map((photo) => (
            <figure key={photo.url} className="flex flex-col gap-1">
              <img src={photo.url} alt={ORIENTATION_LABELS[photo.orientation] ?? photo.orientation} className="aspect-[4/3] w-full rounded-lg object-cover" />
              <figcaption>
                <Typography variant="small" className="text-neutral-dark-gray">
                  {ORIENTATION_LABELS[photo.orientation] ?? photo.orientation}
                </Typography>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {["Documents accepted", "Walk-around and VIN photo checked"].map((label) => (
          <span key={label} className="inline-flex items-center gap-1.5 rounded-full bg-success-light px-3 py-1 text-success-dark">
            <Icon name="TickCircle" variant="Bold" size="sm" />
            <Typography variant="small">{label}</Typography>
          </span>
        ))}
      </div>
    </div>

    <div className="mt-4 flex flex-col-reverse gap-3 xl:flex-row xl:justify-between xl:gap-4">
      <Button type="button" variant="custom" size="xs" className="text-neutral-silver-gray underline xl:w-auto" disabled={saving} onClick={onFinishLater}>
        {saving ? <Spinner /> : "Finish later"}
      </Button>
      <div className="flex flex-col-reverse gap-3 xl:flex-row xl:gap-4">
        <Button type="button" variant="secondary" size="lg" className="w-full xl:w-auto" startIcon="ArrowLeft" onClick={onBack}>
          Back
        </Button>
        <Button type="button" variant="primary" size="lg" className="w-full xl:w-auto" onClick={onConfirm}>
          {feeLabel ? `Add vehicle · ${feeLabel}` : "Add vehicle"}
        </Button>
      </div>
    </div>
  </div>
);

/** While the vehicle is created: the details, drivers and verification are
 *  being saved to the account (DEV-1903). */
export const AddingVehicle = () => (
  <div className="flex flex-col items-center gap-4 py-24 text-center" role="status">
    <Icon name="rm-logo-emblem" isCustom size={96} />
    <Spinner />
    <Typography variant="h3">Adding your vehicle</Typography>
    <Typography variant="paragraph" className="text-neutral-dark-gray">
      Saving the details, drivers and verification to your account…
    </Typography>
  </div>
);
